import { LitElement, css, html, classMap, createRef } from '@hh/global.js'

class HHSearchForm extends LitElement {
  static properties = {
    action: {
      String,
    },
    value: {
      String,
    },
    placeholder: {
      String,
    },
    hiddenPlaceholder: {
      String,
    },
    searchButtonLabel: {
      type: String,
      attribute: 'search-button-label',
    },
    open: { type: Boolean, attribute: false },
    lastSearchTerm: { type: String, attribute: false },
    searchType: { type: String, attribute: false },
    loading: { type: Boolean, attribute: false },
    manufactures: { type: Array, attribute: false },
    manufacturesRender: { type: Array, attribute: false },
    results: { type: Array, attribute: false },
    resultsOpen: { type: Boolean, attribute: false },
    resultsUrl: { type: String, attribute: false },
    currentIndex: { type: Number, attribute: false },
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.uuid = Math.random() * (1000000 - 1) + 1
    this.currentIndex = 0
    this.open = false
    this.lastSearchTerm = ''
    this.secondSearchString = 'Manufacturers'
    this.searchType = 'Products'
    this.loading = false
    this.resultsOpen = false
    this.results = []
    this.manufactures = []
    this.manufacturesRender = []
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener('keydown', this.handleKeyNavigation)
    document.addEventListener(
      'focusDesktopSearchForm',
      this.focusDesktopSearchForm.bind(this)
    )
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.handleKeyNavigation)
    document.removeEventListener(
      'focusDesktopSearchForm',
      this.focusDesktopSearchForm.bind(this)
    )
    super.disconnectedCallback()
  }

  handleKeyNavigation(e) {
    const resultsList = this.shadowRoot.querySelectorAll(
      '.header-search__results-inner li a'
    )
    const numResults = resultsList.length

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (this.currentIndex < numResults - 1) {
        this.currentIndex++
      } else {
        this.currentIndex = 0
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (this.currentIndex > 0) {
        this.currentIndex--
      } else {
        this.currentIndex = numResults - 1
      }
    } else {
      return
    }

    resultsList.forEach((item) => {
      item.blur()
    })

    resultsList[this.currentIndex].focus()
  }

  resetData() {
    this.resultsOpen = false
    this.results = []
    this.manufactures = []
    this.manufacturesRender = []
  }

  handleKeyUp(e) {
    if (e.key === 'Enter') {
      const searchTerm = e.target.value.trim();
      if (searchTerm.length > 0) {
        const searchParams = new URLSearchParams({
          type: 'product',
          q: searchTerm
        });
        const searchUrl = `/search?${searchParams.toString()}`;
        window.location.href = searchUrl;
      }
    } else {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }

      const searchTerm = e.target.value.trim()
      if (searchTerm.length > 2) {
        this.searchTimer = setTimeout(() => {
          this.fetchSearchResults({ term: searchTerm })
          this.resultsOpen = true
        }, 200)
      } else {
        this.resetData()
      }
    }
  }

  formatManufactures(manufactures) {
    const vendorCount = {}
    this.manufacturesRender = []

    manufactures.forEach((item) => {
      if (vendorCount[item.vendor]) {
        vendorCount[item.vendor]++
      } else {
        vendorCount[item.vendor] = 1
      }
    })

    const newArray = Object.entries(vendorCount)
      .map(([vendor, items]) => {
        return { vendor, items }
      })
      .sort(function (a, b) {
        return b.items - a.items
      })

    this.manufacturesRender = newArray.splice(0, 5)
  }

  async fetchSearchResults({ term, page = 1 }) {
    if (this.searchType === this.secondSearchString) {
      if (this.manufactures.length > 0 && term === this.lastSearchTerm) return

      this.lastSearchTerm = term
      this.loading = true

      try {
        const results = await this.getResults({ term, page })
        this.manufactures = results

        if (results.length < results.results_count) {
          const nextPageResults = await this.getResults({
            term,
            page: page + 1,
          })
          this.manufactures = this.manufactures.concat(nextPageResults)

          this.formatManufactures(this.manufactures)
          this.loading = false
        } else {
          this.formatManufactures(this.manufactures)
          this.loading = false
        }
      } catch (error) {
        console.error('Error fetching search results:', error)
      }
    } else {
      if (this.results.length === 0 || term !== this.lastSearchTerm) {
        this.lastSearchTerm = term
        this.loading = true

        try {
          const results = await this.getResults({ term, page })
          this.results = results

          if (results.length >= 50) {
            const nextPageResults = await this.getResults({
              term,
              page: page + 1,
            })
            this.results = this.results.concat(nextPageResults)
          }
          this.manufactures = []
          this.loading = false
        } catch (error) {
          console.error('Error fetching search results:', error)
        }
      }
    }
  }

  async getResults({ term, page }) {
    const url =
      this.searchType === this.secondSearchString
        ? `/search?type=product&q=${term}*&view=json-api`
        : `/search?page=${page}&type=product&q=${encodeURIComponent(
          term
        )}*&view=json-api`
    this.resultsUrl = `/search?page=${page}&type=product&q=${encodeURIComponent(
      term
    )}`

    const response = await fetch(url)
    const data = await response.json()

    return data.results
  }

  handleSubmit(e) {
    e.preventDefault()
    this.fetchSearchResults({ term: this.lastSearchTerm })
  }

  toggleSearchType(e) {
    if (!e.target.textContent) return
    const buttonContent = e.target.textContent.trim()
    this.searchType = buttonContent

    this.fetchSearchResults({ term: this.lastSearchTerm })
  }

  focusDesktopSearchForm() {
    this.shadowRoot.querySelector('#Search-In-Modal').focus()
  }

  render() {
    return html`
      <!-- @ts-ignore no-incompatible-type-binding -->
      <slot
        name="form"
        @keyup="${this.closeWithEscape}"
        @click="${this.closeFromDetails}"
      >
        <form
          class="header-search__form"
          action="${this.action}"
          method="get"
          role="search"
          @submit="${this.handleSubmit}"
        >
          <div class="field header-search__field">
            ${this.hiddenPlaceholder
        ? html`<label class="visually-hidden" for="Search-In-Modal">${this.hiddenPlaceholder}</label>`
        : null}
            <input
              name="q"
              id="Search-In-Modal"
              class="header-search__input"
              type="search"
              autocomplete="off"
              value="${this.value}"
              placeholder="${this.placeholder}"
              @keyup="${this.handleKeyUp}"
              @change="${this.handleKeyUp}"
              @search="${this.handleKeyUp}"
            />
          </div>
        </form>
      </slot>
      <slot name="results" @toggleSearchType="${this.toggleSearchType}">
        <div
          class="header-search__results ${classMap({
          active: this.resultsOpen,
        })}"
        >
          <div class="header-search__header">
            <hh-button
              theme="tertiary"
              custom:click="toggleSearchType"
              class="${classMap({ active: this.searchType === 'Products' })}"
              underline="false"
              data-rerender
            >
              <span class="count">Products</span>
            </hh-button>
            <hh-button
              theme="tertiary"
              custom:click="toggleSearchType"
              class="${classMap({
          active: this.searchType === this.secondSearchString,
        })}"
              underline="false"
              data-rerender
            >
              <span class="count">Manufacturers</span>
            </hh-button>
          </div>

          <p class="header-search__see-all header-search__padding ${classMap({
          hidden: this.searchType === this.secondSearchString,
        })}"
          >
            ${this.resultsUrl
        ? html`
                <a href="${this.resultsUrl}" class="c-night">
                  See all results (${this.results.length})
                </a>`
        : null
      }
          </p>

          ${this.loading
        ? html`<p class="header-search__loading">Loading...</p>`
        : ''}

          <ul
            role="list"
            aria-activedescendant="${this.uuid}-list-${this.currentIndex}"
            aria-hidden="${!this.resultsOpen}"
            class="header-search__results-inner list-unstyled"
          >
            ${this.searchType === this.secondSearchString
        ? this.manufacturesRender.map((element, index) => {
          return html`
            <li
              id="${this.uuid}-list-${index}"
              aria-selected="${this.currentIndex === index}"
              class="header-search__padding"
            >
              <a
                href="/search?type=product&q=${this
              .lastSearchTerm}*+vendor:${element.vendor}"
                class="header-search__vendor-item"
              >
                <div>${element.vendor}</div>
                <div class="caption">
                  ${element.items}
                  ${element.items > 1 ? 'items' : 'item'}
                </div>
              </a>
            </li>`
        })
        : this.results.map((element, index) => {
          if (element.vendor !== 'Used') {
            return html`
              <li
                id="${this.uuid}-list-${index}"
                aria-selected="${this.currentIndex === index}"
                class="header-search__padding"
              >
                <a href="${element.url}" class="header-search__item">
                  <div class="header-search__image">
                    <img
                      src="${element.thumbnail}"
                      alt="${element.title}"
                    />
                  </div>
                  <div class="header-search__data">
                    <p>${element.vendor}</p>
                    <p>${element.title}</p>
                    <p>${element.tags.includes('Coming Soon')
                ? 'Coming Soon'
                : element.price}</p>
                  </div>
                </a>
              </li>
            `
          }
        })}
          </ul>
        </div>
      </slot>
    `
  }

  static styles = css`
    *:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .header-search__form {
      width: 100%;
    }

    .header-search__input {
      flex: 1;
      width: 100%;
      border: none;
      max-width: 165px;
      background: transparent;
      font: var(--hh-h5-font);
    }

    .header-search__label {
      display: block;
      margin-bottom: 2rem;
      color: var(--hh-color-subdued);
    }

    .header-search__results {
      left: 0;
      top: 100%;
      width: 369px;
      z-index: 10;
      display: none;
      position: absolute;
      background: #e6e6e6;
      padding: 18px 18px 0;
      box-sizing: border-box;
    }

    .header-search__results.active {
      display: block;
    }

    .header-search__see-all.hidden {
      font-size: 0;
      visibility: hidden;
    }

    .header-search__see-all a {
      color: var(--hh-color-night);
      text-decoration: none;
    }

    .header-search__see-all a:hover {
      text-decoration: underline;
    }

    .header-search__header {
      display: flex;
      column-gap: 3rem;
    }

    .header-search__loading {
      padding-bottom: 0.5rem 1.2rem;
    }

    .header-search__header hh-button {
      --hh-font: 500 12px/19px 'Monosten E', sans-serif;
    }

    .header-search__header hh-button.active span {
      text-decoration: underline;
    }

    .header-search__results-inner {
      margin: 0;
      padding: 0;
      list-style: none;
      max-height: 65vh;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .header-search__results-inner li {
      padding-top: 1.2rem;
      padding-bottom: 1.8rem;
      border-top: 1px solid var(--hh-color-dark-grey);
    }

    .header-search__results-inner li a:hover {
      color: var(--hh-color-darker-grey);
    }

    .header-search__results-inner li:last-child {
      padding-bottom: 12rem;
    }

    @media (min-width: 750px) {
      .header-search__results-inner li:last-child {
        padding-bottom: 1.8rem;
      }
    }

    .header-search__results-inner .header-search__item {
      display: flex;
      align-items: start;
      column-gap: 1.8rem;
      text-decoration: none;
      color: var(--hh-color-night);
    }
    .header-search__results-inner .header-search__vendor-item {
      display: flex;
      row-gap: 1rem;
      flex-direction: column;
      text-decoration: none;
      color: var(--hh-color-night);
    }

    .header-search__results a:focus-visible {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .header-search__results-inner .header-search__item:hover {
      color: var(--hh-color-darker-grey);
    }

    .header-search__results-inner .header-search__item .header-search__image {
      width: 85px;
      height: 85px;
    }

    .header-search__results-inner
      .header-search__item
      .header-search__image
      img {
      width: 85px;
      display: block;
    }

    .header-search__results-inner .header-search__data {
      display: flex;
      row-gap: 1rem;
      min-width: 0;
      min-height: 0;
      flex: 1 1 auto;
      flex-direction: column;
    }

    .header-search__results-inner .header-search__data p {
      margin: 0;
    }

    .header-search__padding {
      padding-left: 18px;
      padding-right: 3px;
    }

    .caption {
      font: var(--hh-body-caption);
    }

    :host([placement='mobile']) .header-search__input {
      width: 100%;
      max-width: 100%;
    }

    :host([placement='mobile']) .header-search__results {
      right: 0;
      top: 4.8rem;
      width: auto;
      padding-bottom: 6rem;
    }
  `
}

customElements.define('hh-search-form', HHSearchForm)
